import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Copy, Check, Ban, Link2, Users as UsersIcon } from "lucide-react";
import {
    adminListCodes, adminCreateCode, adminRevokeCode, adminCodeUsers,
} from "@/lib/api";
import { Card, SectionTitle, Spinner, EmptyState, Table, StatusPill, KawaiiModal, ConfirmDialog } from "./kit";

const fmtDate = (v) => {
    if (!v) return "—";
    try { return new Date(v).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); }
    catch { return v; }
};

export default function CodesSection() {
    const [codes, setCodes] = useState(null);
    const [createOpen, setCreateOpen] = useState(false);
    const [maxUses, setMaxUses] = useState("1");
    const [expires, setExpires] = useState("");
    const [creating, setCreating] = useState(false);
    const [copied, setCopied] = useState(null);
    const [copiedLink, setCopiedLink] = useState(null);
    const [revokeTarget, setRevokeTarget] = useState(null);
    const [usersModal, setUsersModal] = useState(null); // {code, used_by}

    const load = useCallback(async () => {
        try {
            const r = await adminListCodes();
            setCodes(r.codes || []);
        } catch { /* auth gate */ }
    }, []);

    useEffect(() => { load(); }, [load]);

    const create = async () => {
        const n = parseInt(maxUses, 10);
        if (Number.isNaN(n) || n < 1) { toast.error("max uses must be at least 1 🍡"); return; }
        setCreating(true);
        try {
            const exp = expires ? `${expires}T23:59:59+00:00` : null;
            const doc = await adminCreateCode(n, exp);
            toast.success(`code ${doc.code} is ready ✨`);
            setCreateOpen(false);
            setMaxUses("1"); setExpires("");
            await load();
        } catch { toast.error("couldn't make that code, try again"); }
        finally { setCreating(false); }
    };

    const revoke = async () => {
        const code = revokeTarget;
        setRevokeTarget(null);
        try {
            await adminRevokeCode(code);
            toast.success(`${code} revoked 🍡`);
            await load();
        } catch { toast.error("couldn't revoke that code"); }
    };

    const copy = async (code) => {
        try {
            await navigator.clipboard.writeText(code);
            setCopied(code);
            setTimeout(() => setCopied(null), 1500);
        } catch { toast.error("couldn't copy — long-press to copy manually"); }
    };

    const inviteLink = (code) => `${window.location.origin}/beta?code=${encodeURIComponent(code)}`;

    const copyLink = async (code) => {
        try {
            await navigator.clipboard.writeText(inviteLink(code));
            setCopiedLink(code);
            toast.success("invite link copied 🍡 send it to a friend");
            setTimeout(() => setCopiedLink(null), 1500);
        } catch { toast.error("couldn't copy — long-press to copy manually"); }
    };

    const viewUsers = async (code) => {
        try {
            const r = await adminCodeUsers(code);
            setUsersModal({ code: r.code, used_by: r.used_by || [] });
        } catch { toast.error("couldn't load redemptions"); }
    };

    if (codes === null) return <Spinner label="gathering invite codes…" />;

    return (
        <div data-testid="admin-codes">
            <div className="flex items-center justify-between mb-4">
                <SectionTitle hint="Share these with friends to let them into the beta.">
                    Invite codes
                </SectionTitle>
                <button
                    onClick={() => setCreateOpen(true)}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold text-cream bg-terracotta hover:bg-terracotta-hover transition-colors shrink-0"
                    data-testid="codes-generate-btn"
                >
                    <Plus className="w-4 h-4" /> New code
                </button>
            </div>

            {codes.length === 0 ? (
                <Card className="p-2">
                    <EmptyState testid="codes-empty">
                        no codes yet — make one and share the love 🍡
                    </EmptyState>
                </Card>
            ) : (
                <Table head={["Code", "Uses", "Status", "Expires", "Created", ""]} testid="codes-table">
                    {codes.map((c) => (
                        <tr key={c.code} className="hover:bg-surface/50 transition-colors" data-testid={`code-row-${c.code}`}>
                            <td className="px-4 py-3">
                                <div className="flex items-center gap-2">
                                    <span className="font-display text-warm-900">{c.code}</span>
                                    <button
                                        onClick={() => copy(c.code)}
                                        className="p-1 rounded-full hover:bg-surface transition-colors"
                                        aria-label={`Copy ${c.code}`}
                                        data-testid={`code-copy-${c.code}`}
                                    >
                                        {copied === c.code
                                            ? <Check className="w-3.5 h-3.5 text-sage" />
                                            : <Copy className="w-3.5 h-3.5 text-warm-500" />}
                                    </button>
                                </div>
                            </td>
                            <td className="px-4 py-3 text-warm-800">{c.uses_count} / {c.max_uses}</td>
                            <td className="px-4 py-3"><StatusPill status={c.status} /></td>
                            <td className="px-4 py-3 text-warm-500">{fmtDate(c.expires_at)}</td>
                            <td className="px-4 py-3 text-warm-500">{fmtDate(c.created_at)}</td>
                            <td className="px-4 py-3">
                                <div className="flex items-center gap-1 justify-end">
                                    {c.status === "active" && (
                                        <button
                                            onClick={() => copyLink(c.code)}
                                            className="flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold text-warm-800 bg-surface hover:bg-sage/25 transition-colors"
                                            aria-label={`Copy invite link for ${c.code}`}
                                            title={inviteLink(c.code)}
                                            data-testid={`code-copy-link-${c.code}`}
                                        >
                                            {copiedLink === c.code
                                                ? <><Check className="w-3.5 h-3.5 text-sage" /> copied</>
                                                : <><Link2 className="w-3.5 h-3.5 text-warm-500" /> copy link</>}
                                        </button>
                                    )}
                                    <button
                                        onClick={() => viewUsers(c.code)}
                                        className="p-1.5 rounded-full hover:bg-surface transition-colors"
                                        aria-label="View users"
                                        data-testid={`code-view-users-${c.code}`}
                                    >
                                        <UsersIcon className="w-4 h-4 text-warm-500" />
                                    </button>
                                    {c.status !== "revoked" && (
                                        <button
                                            onClick={() => setRevokeTarget(c.code)}
                                            className="p-1.5 rounded-full hover:bg-terracotta-soft/40 transition-colors"
                                            aria-label="Revoke code"
                                            data-testid={`code-revoke-${c.code}`}
                                        >
                                            <Ban className="w-4 h-4 text-terracotta" />
                                        </button>
                                    )}
                                </div>
                            </td>
                        </tr>
                    ))}
                </Table>
            )}

            {/* Create modal */}
            <KawaiiModal open={createOpen} onClose={() => setCreateOpen(false)} title="Make a new invite code" testid="create-code-modal">
                <label className="block text-warm-800 text-sm font-semibold mb-1">Max uses</label>
                <input
                    type="number" min="1" value={maxUses}
                    onChange={(e) => setMaxUses(e.target.value)}
                    className="w-full px-4 py-3 rounded-2xl bg-surface border border-warm-300/40 text-warm-900 focus:outline-none focus:ring-2 focus:ring-terracotta/40 mb-4"
                    data-testid="create-code-max-uses"
                />
                <label className="block text-warm-800 text-sm font-semibold mb-1">Expires (optional)</label>
                <input
                    type="date" value={expires}
                    onChange={(e) => setExpires(e.target.value)}
                    className="w-full px-4 py-3 rounded-2xl bg-surface border border-warm-300/40 text-warm-900 focus:outline-none focus:ring-2 focus:ring-terracotta/40 mb-2"
                    data-testid="create-code-expires"
                />
                <p className="text-warm-500 text-xs mb-6">leave blank = never expires 🌸</p>
                <div className="flex gap-3 justify-end">
                    <button onClick={() => setCreateOpen(false)} className="px-4 py-2 rounded-full text-sm font-semibold text-warm-800 bg-surface hover:bg-surface-hover" data-testid="create-code-cancel">Never mind</button>
                    <button onClick={create} disabled={creating} className="px-4 py-2 rounded-full text-sm font-semibold text-cream bg-terracotta hover:bg-terracotta-hover disabled:opacity-60" data-testid="create-code-submit">
                        {creating ? "making…" : "Generate"}
                    </button>
                </div>
            </KawaiiModal>

            {/* Redeemers modal */}
            <KawaiiModal open={!!usersModal} onClose={() => setUsersModal(null)} title={usersModal ? `Who used ${usersModal.code}` : ""} testid="code-users-modal">
                {usersModal && usersModal.used_by.length === 0 ? (
                    <EmptyState testid="code-users-empty">nobody's used this one yet 🍡</EmptyState>
                ) : (
                    <ul className="space-y-2 max-h-72 overflow-y-auto">
                        {usersModal?.used_by.map((u, i) => (
                            <li key={i} className="flex items-center justify-between bg-surface/60 rounded-xl px-3 py-2" data-testid={`code-user-${i}`}>
                                <span className="text-warm-900 text-sm">{u.email}</span>
                                <span className="text-warm-500 text-xs">{fmtDate(u.used_at)}</span>
                            </li>
                        ))}
                    </ul>
                )}
            </KawaiiModal>

            <ConfirmDialog
                open={!!revokeTarget}
                onClose={() => setRevokeTarget(null)}
                onConfirm={revoke}
                title="Revoke this code?"
                message="Are you sure? Nobody new will be able to use this code to join 🍡"
                confirmLabel="Revoke it"
                danger
                testid="revoke-confirm"
            />
        </div>
    );
}
